import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Clapperboard, ClipboardList, Camera, Mic, Lightbulb, Palette,
  Shirt, Scissors, Plus, Calendar, Phone, BadgeCheck, Trash2, Search,
  MessageCircle, Lock, ArrowLeft, MapPin, HardDrive, Film, Radar,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import TVFormatPanel from "@/pages/tv/TVFormatPanel";
import MultiUnitDialog from "@/pages/tv/MultiUnitDialog";
import ConvertTypeModal from "@/pages/tv/ConvertTypeModal";
import EpisodeSignoffLedger from "@/pages/tv/EpisodeSignoffLedger";
import ScriptChangeLog from "@/pages/tv/ScriptChangeLog";
import CastApprovalLedger from "@/pages/tv/CastApprovalLedger";
import ContinuityBook from "@/pages/continuity/ContinuityBook";
import TodaysCallSheet from "@/pages/continuity/TodaysCallSheet";

const TV_PROJECT_TYPES = new Set([
  "tv_episodic", "tv_daily_soap", "format_reality",
  "tv", "format_show", // legacy aliases
]);

/** Project dashboard — 8 department tiles. Click a tile to expand its
 *  crew roster + open the "Add Crew" modal.
 *
 *  This is the central operating surface once payment unlocks the project.
 *  Each department is isolated by default — dept heads will be added
 *  in Slice 2 with a permission gate around `expandedDept`.
 */

const DEPT_ICONS = {
  direction:  Clapperboard,
  production: ClipboardList,
  camera:     Camera,
  sound:      Mic,
  light:      Lightbulb,
  art:        Palette,
  costume:    Shirt,
  editorial:  Scissors,
};

const DEPT_COLOR = {
  direction:  "#D9383A",
  production: "#F5B82E",
  camera:     "#6B4EFF",
  sound:      "#10B981",
  light:      "#F59E0B",
  art:        "#EC4899",
  costume:    "#8B5CF6",
  editorial:  "#06B6D4",
};

export default function ProjectDashboard() {
  const { id } = useParams();
  const nav = useNavigate();
  const { featureFlags } = useAuth();
  const paymentsOff = featureFlags && featureFlags.payments_enabled === false;
  const [project, setProject]       = useState(null);
  const [departments, setDepartments] = useState([]);
  const [expanded, setExpanded]     = useState(null);    // dept key currently expanded
  const [addOpen, setAddOpen]       = useState(false);
  const [addDept, setAddDept]       = useState("");
  const [preprod, setPreprod]       = useState(null);
  const [postprod, setPostprod]     = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);
  const [unitsOpen, setUnitsOpen]   = useState(false);

  const load = async () => {
    try {
      const [{ data: projResp }, { data: depts }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get("/meta/departments"),
      ]);
      // Endpoint returns {project, crew, crew_count}. Unwrap the project.
      setProject(projResp.project || projResp);
      setDepartments(depts);
      // Workspace counters — non-blocking; ignore errors (403 for non-crew is fine)
      api.get(`/projects/${id}/preprod/summary`).then(r => setPreprod(r.data)).catch(() => {});
      api.get(`/projects/${id}/postprod/summary`).then(r => setPostprod(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load project");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openAddCrew = (deptKey) => {
    setAddDept(deptKey);
    setAddOpen(true);
  };

  const removeCrew = async (deptKey, rowId) => {
    if (!confirm("Remove this crew member from the department?")) return;
    try {
      await api.delete(`/projects/${id}/departments/${deptKey}/crew/${rowId}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
  };

  if (!project) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 text-center text-white/55">
        Loading project…
      </div>
    );
  }

  const crewByDept = project.department_crew || {};
  const totalCrew = Object.values(crewByDept).reduce((a, b) => a + (b?.length || 0), 0);
  // Paywall is bypassed platform-wide while PAYMENTS_ENABLED=false on the
  // server. Legacy `awaiting_payment` rows created before the toggle stay
  // functional too — no manual migration required.
  const isLocked = !paymentsOff && project.status === "awaiting_payment";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="project-dashboard">
      <button
        onClick={() => nav("/projects")}
        className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
        data-testid="back-to-projects"
      >
        <ArrowLeft size={12} /> All Projects
      </button>

      {/* Header */}
      <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/55">
              {project.type_label || project.project_type}
            </span>
            <StatusPill status={project.status} />
            <button
              onClick={() => setConvertOpen(true)}
              className="text-[10px] tracking-[0.2em] uppercase text-white/45 hover:text-white inline-flex items-center gap-1 border border-white/10 rounded-full px-2.5 py-0.5 hover:border-white/30 transition-colors"
              data-testid="change-project-type-btn"
              title="Change project type — data is preserved"
            >
              <Wand2 size={10} /> Change type
            </button>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight" data-testid="project-title">
            {project.title}
          </h1>
          <div className="mt-2 text-sm text-white/55">
            {project.location || "No location set"} · Crew: {totalCrew}
          </div>
        </div>
      </div>

      {isLocked && (
        <div className="mt-6 cs-card rounded-2xl p-5 flex items-start gap-3 border border-amber-500/30" data-testid="locked-banner">
          <Lock size={18} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-200">Project locked — awaiting payment</div>
            <div className="text-xs text-white/65 mt-1">
              Pay for the project tier to unlock the full dashboard and start adding crew.
            </div>
          </div>
          <Button
            onClick={() => nav("/pricing")}
            className="btn-themed h-9 px-5 rounded-full text-[10px] uppercase tracking-[0.22em]"
            data-testid="unlock-project-cta"
          >
            Unlock Project
          </Button>
        </div>
      )}

      {/* TV format tiles — only shown for TV / Daily Soap / Format-Reality projects */}
      {!isLocked && TV_PROJECT_TYPES.has(project.project_type) && (
        <>
          <TVFormatPanel
            project={project}
            onRefresh={load}
            onOpenUnits={() => setUnitsOpen(true)}
          />
          <div className="mt-8" data-testid="tv-script-log-slot">
            <ScriptChangeLog projectId={id} viewerRole="ph_owner" />
          </div>
          <div className="mt-8" data-testid="tv-episode-ledger-slot">
            <EpisodeSignoffLedger projectId={id} viewerRole="ph_owner" />
          </div>
          <div className="mt-8" data-testid="tv-cast-approval-slot">
            <CastApprovalLedger projectId={id} viewerRole="ph_owner" />
          </div>
        </>
      )}

      {/* Continuity Book — universal across every project type (film,
          ad_film, tv_*, web_series, short_film, event, micro_drama). */}
      {!isLocked && (
        <>
          <div className="mt-8" data-testid="todays-callsheet-slot">
            <TodaysCallSheet projectId={id} />
          </div>
          <div className="mt-8" data-testid="continuity-book-slot">
            <ContinuityBook projectId={id} />
          </div>
        </>
      )}

      {/* Workspaces strip — Pre-Prod + Post-Prod (shared across all project types) */}
      {!isLocked && (
        <div className="mt-8" data-testid="workspaces-strip">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3">Workspaces</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <WorkspaceTile
              testid="workspace-tile-recce"
              onClick={() => nav(`/p/${id}/recce`)}
              Icon={MapPin}
              color="#F5B82E"
              phase="Pre-Prod"
              label="Recce · Locations"
              primary={preprod?.locations?.total ?? "—"}
              secondary={preprod ? `${preprod.locations.finalized || 0} finalized · ${preprod.locations.shortlisted || 0} in review` : "Location scouting"}
            />
            <WorkspaceTile
              testid="workspace-tile-shots"
              onClick={() => nav(`/p/${id}/shots`)}
              Icon={Clapperboard}
              color="#D9383A"
              phase="Pre-Prod"
              label="Shot List"
              primary={preprod?.shots?.total ?? "—"}
              secondary={preprod ? `${preprod.shots.shot || 0} shot · ${preprod.shots.planned || 0} planned` : "Scene-by-scene shot plan"}
            />
            <WorkspaceTile
              testid="workspace-tile-heatmap"
              onClick={() => nav(`/p/${id}/heatmap`)}
              Icon={Radar}
              color="#10B981"
              phase="On-Set"
              label="Attendance Heatmap"
              primary=""
              secondary="Live crew geo-attendance for producers + ADs"
            />
            <WorkspaceTile
              testid="workspace-tile-rushes"
              onClick={() => nav(`/p/${id}/rushes`)}
              Icon={HardDrive}
              color="#0EA5E9"
              phase="Post-Prod"
              label="Rushes"
              primary={postprod?.rushes?.total ?? "—"}
              secondary={postprod ? `${postprod.rushes.total_gb || 0} GB · ${postprod.rushes.verified || 0} verified` : "Shoot-day handoff tracker"}
            />
            <WorkspaceTile
              testid="workspace-tile-luts"
              onClick={() => nav(`/p/${id}/luts`)}
              Icon={Palette}
              color="#EC4899"
              phase="Post-Prod"
              label="LUTs · Post Assets"
              primary={postprod?.luts?.total ?? "—"}
              secondary={postprod ? `${postprod.luts.signed_off || 0} DoP-signed · ${postprod.luts.pending || 0} pending` : "Colour asset library"}
            />
          </div>
        </div>
      )}

      {/* Department grid */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="dept-grid">
        {departments.map((d) => {
          const Icon = DEPT_ICONS[d.key] || ClipboardList;
          const color = DEPT_COLOR[d.key] || "#888";
          const crew = crewByDept[d.key] || [];
          return (
            <button
              key={d.key}
              onClick={() => setExpanded(expanded === d.key ? null : d.key)}
              disabled={isLocked}
              className={`cs-card rounded-2xl p-5 text-left relative overflow-hidden group transition-transform hover:-translate-y-0.5 ${
                expanded === d.key ? "ring-2" : ""
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              style={expanded === d.key ? { boxShadow: `0 0 0 2px ${color}` } : {}}
              data-testid={`dept-tile-${d.key}`}
            >
              <div
                className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-15 blur-2xl pointer-events-none transition-opacity group-hover:opacity-25"
                style={{ background: color }}
              />
              <div className="flex items-center justify-between relative">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: `${color}24`, color: color }}
                >
                  <Icon size={18} />
                </div>
                <div className="text-3xl font-display font-bold" style={{ color: color }}>
                  {crew.length}
                </div>
              </div>
              <div className="mt-4 text-xs tracking-[0.25em] uppercase text-white/85 font-semibold">
                {d.label}
              </div>
              <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-white/45">
                {crew.length === 0 ? "Empty" : `${crew.filter((c) => c.mode === "finalized").length} finalized · ${crew.filter((c) => c.mode === "discussion").length} in talks`}
              </div>
            </button>
          );
        })}
      </div>

      {/* Expanded dept panel */}
      {expanded && !isLocked && (
        <div className="mt-6 cs-card rounded-2xl p-6" data-testid="dept-panel">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: `${DEPT_COLOR[expanded]}24`,
                  color: DEPT_COLOR[expanded],
                }}
              >
                {(() => { const Icon = DEPT_ICONS[expanded] || ClipboardList; return <Icon size={18} />; })()}
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Department</div>
                <h2 className="font-display text-xl uppercase tracking-tight">
                  {departments.find((d) => d.key === expanded)?.label}
                </h2>
              </div>
            </div>
            <Button
              onClick={() => openAddCrew(expanded)}
              className="btn-themed h-10 px-5 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold"
              data-testid={`add-crew-${expanded}`}
            >
              <Plus size={13} className="mr-1.5" /> Add Crew
            </Button>
          </div>

          <div className="mt-5">
            {(crewByDept[expanded] || []).length === 0 ? (
              <div className="text-sm text-white/55 py-8 text-center" data-testid="dept-empty">
                No one in this department yet. Click <strong>Add Crew</strong> to find someone by phone, name or association ID.
              </div>
            ) : (
              <div className="space-y-2.5" data-testid="dept-crew-list">
                {(crewByDept[expanded] || []).map((c) => (
                  <CrewRow key={c.id} c={c} onRemove={() => removeCrew(expanded, c.id)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <AddCrewDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={id}
        deptKey={addDept}
        deptLabel={departments.find((d) => d.key === addDept)?.label || ""}
        onAdded={async () => { await load(); }}
      />

      <ConvertTypeModal
        open={convertOpen}
        onOpenChange={setConvertOpen}
        project={project}
        onConverted={load}
      />

      <MultiUnitDialog
        open={unitsOpen}
        onOpenChange={setUnitsOpen}
        projectId={id}
        onSaved={load}
      />
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    awaiting_payment: { bg: "bg-amber-500/15", fg: "text-amber-300",  label: "Awaiting Payment" },
    active:           { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Active" },
    wrapped:          { bg: "bg-white/10",      fg: "text-white/55",   label: "Wrapped" },
  };
  const s = map[status] || { bg: "bg-white/10", fg: "text-white/55", label: status };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function CrewRow({ c, onRemove }) {
  return (
    <div className="rounded-xl p-3 pl-2 bg-white/[0.04] border border-white/10 flex items-center gap-3 hover:bg-white/[0.06] transition-colors" data-testid={`crew-row-${c.id}`}>
      <Avatar src={c.avatar_url} name={c.name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-white truncate">{c.name}</span>
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/55">
            {c.role_label}
          </span>
          {c.membership_number && (
            <span className="text-[10px] font-mono text-white/40 px-1.5 py-0.5 rounded bg-white/5">
              {c.membership_number}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-3 text-[11px] text-white/45">
          {c.phone && <span className="flex items-center gap-1"><Phone size={10} />{c.phone}</span>}
          {c.daily_rate > 0 && <span>₹{Number(c.daily_rate).toLocaleString("en-IN")}/day</span>}
          {!c.is_platform_member && (
            <span className="px-1.5 py-0.5 rounded bg-white/10 text-white/50 text-[9px] tracking-[0.18em] uppercase">
              Off-platform
            </span>
          )}
        </div>
      </div>
      <ModePill mode={c.mode} date={c.finalize_date} />
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-300 transition-opacity"
        data-testid={`remove-crew-${c.id}`}
        aria-label="Remove crew"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function ModePill({ mode, date }) {
  if (mode === "finalized") {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] tracking-[0.18em] uppercase">
        <BadgeCheck size={12} />
        <span>Finalized</span>
        {date && <span className="opacity-70">· {fmtDate(date)}</span>}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 text-[10px] tracking-[0.18em] uppercase">
      <MessageCircle size={12} />
      <span>In Talks</span>
    </div>
  );
}

function Avatar({ src, name }) {
  if (src) {
    return (
      <img src={src} alt={name}
        className="w-9 h-9 rounded-full object-cover border border-white/15" />
    );
  }
  const initials = name?.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-[11px] font-semibold text-white/70 border border-white/15">
      {initials}
    </div>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
}

/* ────────────────────────────── ADD CREW MODAL ───────────────────────── */

function AddCrewDialog({ open, onOpenChange, projectId, deptKey, deptLabel, onAdded }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);   // a result row, or null for off-platform
  const [roles, setRoles] = useState([]);
  // Form state for off-platform add
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [memNo, setMemNo] = useState("");
  const [role, setRole] = useState("");
  const [mode, setMode] = useState("discussion");
  const [finalDate, setFinalDate] = useState("");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/meta/roles").then(({ data }) => setRoles(data))
       .catch((err) => console.warn("[ProjectDashboard] /meta/roles failed:", err?.message));
    // Reset every time it opens
    setQuery(""); setResults([]); setSelected(null);
    setName(""); setPhone(""); setMemNo(""); setRole("");
    setMode("discussion"); setFinalDate(""); setRate("");
  }, [open]);

  // Debounced lookup
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/crew/lookup", { params: { q: query } });
        setResults(data.results || []);
      } catch (err) { console.warn("[ProjectDashboard] crew lookup failed", err); }
    }, 220);
    return () => clearTimeout(t);
  }, [query]);

  const pick = (r) => {
    setSelected(r);
    setName(r.name); setPhone(r.phone); setMemNo(r.membership_number);
    setRole(r.primary_role || "");
  };

  const submit = async () => {
    if (!role) { toast.error("Pick a role"); return; }
    if (mode === "finalized" && !finalDate) { toast.error("Pick a finalize date"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/departments/${deptKey}/crew`, {
        user_id: selected?.id || null,
        name, phone, membership_number: memNo, role,
        mode, finalize_date: finalDate || null,
        daily_rate: rate ? Number(rate) : 0,
      });
      toast.success(mode === "finalized"
        ? `${name} finalized for ${fmtDate(finalDate)}`
        : `${name} added to discussion`);
      onOpenChange(false);
      await onAdded?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add crew");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-lg" data-testid="add-crew-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-tight">
            Add Crew · {deptLabel}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Look up by phone, name or association number. If they&apos;re not on
            the platform yet, enter raw details — they&apos;ll get a notification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Find Crew</Label>
            <div className="mt-2 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                placeholder="Type name, email, phone or membership number…"
                className="h-11 bg-black/40 border-white/15 pl-9"
                data-testid="crew-lookup-input"
              />
            </div>
            {results.length > 0 && !selected && (
              <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-black/40" data-testid="crew-lookup-results">
                {results.map((r) => (
                  <button key={r.id}
                    onClick={() => pick(r)}
                    className="w-full text-left p-2.5 hover:bg-white/[0.06] flex items-center gap-3 border-b border-white/5 last:border-none"
                    data-testid={`crew-lookup-row-${r.id}`}
                  >
                    <Avatar src={r.avatar_url} name={r.name} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{r.name}</div>
                      <div className="text-[11px] text-white/55 flex items-center gap-2">
                        <span>{r.role_label || "—"}</span>
                        {r.membership_number && (
                          <span className="font-mono text-white/40">· {r.membership_number}</span>
                        )}
                      </div>
                    </div>
                    {r.is_member && <BadgeCheck size={14} className="text-emerald-400 shrink-0" />}
                  </button>
                ))}
              </div>
            )}
            {selected && (
              <div className="mt-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3" data-testid="crew-selected">
                <Avatar src={selected.avatar_url} name={selected.name} />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">{selected.name}</div>
                  <div className="text-[11px] text-white/65 font-mono">{selected.membership_number}</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-white/55 text-[10px] uppercase tracking-[0.2em] hover:text-white">
                  Change
                </button>
              </div>
            )}
          </div>

          {!selected && query.length >= 2 && results.length === 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 h-10 bg-black/40 border-white/15"
                  data-testid="crew-offplatform-name" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="mt-2 h-10 bg-black/40 border-white/15"
                  data-testid="crew-offplatform-phone" />
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-2 h-10 bg-black/40 border-white/15" data-testid="crew-role-select">
                  <SelectValue placeholder="Pick a role" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {roles.map((r) => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Daily Rate (₹)</Label>
              <Input type="number" min={0} value={rate} onChange={(e) => setRate(e.target.value)}
                placeholder="optional"
                className="mt-2 h-10 bg-black/40 border-white/15"
                data-testid="crew-rate" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55 mb-2 block">Engagement</Label>
            <div className="grid grid-cols-2 gap-2">
              <ModeButton
                active={mode === "discussion"}
                onClick={() => setMode("discussion")}
                label="In Discussion"
                desc="Soft offer · opens chat"
                color="#F5B82E"
                tid="mode-discussion"
              />
              <ModeButton
                active={mode === "finalized"}
                onClick={() => setMode("finalized")}
                label="Finalize Now"
                desc="Locked in · with date"
                color="#10B981"
                tid="mode-finalized"
              />
            </div>
            {mode === "finalized" && (
              <div className="mt-3">
                <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Finalize Date *</Label>
                <Input type="date" value={finalDate} onChange={(e) => setFinalDate(e.target.value)}
                  className="mt-2 h-10 bg-black/40 border-white/15"
                  data-testid="crew-finalize-date" />
              </div>
            )}
          </div>

          <Button
            onClick={submit} disabled={busy || !role || !name}
            className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
            data-testid="crew-submit"
          >
            {busy ? "Adding…" : (mode === "finalized" ? "Finalize & Add" : "Open Discussion")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ModeButton({ active, onClick, label, desc, color, tid }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-xl border text-left transition-colors ${
        active ? "bg-white/10" : "bg-black/30 hover:bg-white/[0.04]"
      }`}
      style={{ borderColor: active ? color : "rgba(255,255,255,0.1)" }}
      data-testid={tid}
    >
      <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: active ? color : "rgba(255,255,255,0.85)" }}>
        {label}
      </div>
      <div className="text-[10px] text-white/45 mt-1">{desc}</div>
    </button>
  );
}


function WorkspaceTile({ testid, onClick, Icon, color, phase, label, primary, secondary }) {
  return (
    <button onClick={onClick}
      className="cs-card rounded-2xl p-5 text-left relative overflow-hidden group transition-transform hover:-translate-y-0.5"
      style={{ borderColor: `${color}22` }}
      data-testid={testid}>
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-15 blur-2xl pointer-events-none transition-opacity group-hover:opacity-25"
        style={{ background: color }} />
      <div className="flex items-center justify-between relative">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: `${color}24`, color: color }}>
          <Icon size={18} />
        </div>
        <div className="text-3xl font-display font-bold tabular-nums" style={{ color: color }}>
          {primary}
        </div>
      </div>
      <div className="mt-4 flex items-center gap-1.5">
        <span className="text-[9px] tracking-[0.3em] uppercase text-white/40">{phase}</span>
        <span className="text-white/25">·</span>
        <span className="text-xs tracking-[0.22em] uppercase text-white/85 font-semibold">{label}</span>
      </div>
      <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-white/45 truncate">
        {secondary}
      </div>
    </button>
  );
}
